import React from 'react';
import styles from './RadioButton.module.scss';

export interface RadioButtonProps {
    /**
     * Whether the radio button is checked
     */
    checked: boolean;
    /**
     * Whether the radio button is disabled
     */
    disabled?: boolean;
    /**
     * Optional label text for the radio button
     */
    label?: string;
    /**
     * Optional name attribute for the radio button
     */
    name?: string;
    /**
     * Optional value for the radio button
     */
    value?: string;
    /**
     * Callback function when the radio button state changes
     */
    onChange?: (checked: boolean) => void;
    /**
     * Optional className for custom styling
     */
    className?: string;
    /**
     * Optional size of the radio button ('small' | 'medium' | 'large')
     */
    size?: 'small' | 'medium' | 'large';
    labelColor?: string;
}

const RadioButton: React.FC<RadioButtonProps> = ({
    checked,
    disabled = false,
    label,
    name,
    value,
    onChange,
    labelColor,
    className = '',
    size = 'medium',
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) onChange(e.target.checked);
    };

    const handleClick = () => {
        if (checked && onChange) {
            onChange(false);
        }
    };

    return (
        <div
            className={`${styles.radioWrapper} ${styles[size]} ${className}`}
            onClick={handleClick}
            role="radio"
            aria-checked={checked}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <input
                type="radio"
                checked={checked}
                readOnly={!onChange}
                disabled={disabled}
                name={name}
                value={value}
                onChange={onChange ? handleChange : undefined}
                className={disabled ? styles.radioButtonDisabled : styles.radioButton}
            />
            {label && (
                <span
                    style={disabled ? { color: `${labelColor ?? ''}`, cursor: 'not-allowed' } : { color: `${labelColor ?? ''}` }}
                    className={styles.label}
                >
                    {label}
                </span>
            )}
        </div>
    );
};

export default RadioButton;
